import { Injectable } from "@angular/core";


interface DocumentWithStorageAccess extends Document {
    hasStorageAccess(): Promise<boolean>;
    requestStorageAccess(): Promise<void>;
}

interface StorageAccessOutcome {
    ok: boolean;
    reason: 'already-had' | 'granted' | 'denied' | 'timeout' | 'unsupported' | 'top-level' | 'unknown';
}

@Injectable({
    providedIn: 'root'
})

export class StorageAccessManager {
    private readonly DEFAULT_TIMEOUT_MS = 6000;
    private readonly enableLogging = true;

    constructor() { }

    /**
     * Ensures storage access in cross-site iframe contexts.
     *
     * ⚠️  CRITICAL: Must be called within a user gesture (click, tap, keypress, etc.)
     *     Otherwise, browsers will automatically reject the request.
     *
     * @param timeoutMs - Maximum time to wait for storage access request (default: 6000ms)
     * @returns StorageAccessOutcome indicating success or failure reason
     *
     * @example
     * // Call from a click handler:
     * button.addEventListener('click', async () => {
     *     const result = await manager.ensureStorageAccess();
     *     if (result.ok) {
     *         // Proceed with storage operations
     *     }
     * });
     */
    async ensureStorageAccess(timeoutMs: number = this.DEFAULT_TIMEOUT_MS): Promise<StorageAccessOutcome> {
        // 1. Feature detection
        const hasAPI =
            typeof (document as any).requestStorageAccess === 'function' &&
            typeof (document as any).hasStorageAccess === 'function';

        if (!hasAPI) {
            this.log('warn', 'Storage Access API not supported in this browser');
            return { ok: false, reason: 'unsupported' };
        }

        // 2. Check if we're in an iframe (API only works in cross-site iframes)
        if (window.top === window.self) {
            this.log('info', 'Not in iframe context, storage access not needed');
            return { ok: false, reason: 'top-level' };
        }

        const doc = document as DocumentWithStorageAccess;

        // 3. Check if we already have access
        try {
            const hasAccess = await doc.hasStorageAccess();
            if (hasAccess) {
                this.log('info', 'Storage access already granted');
                return { ok: true, reason: 'already-had' };
            }
        } catch (error) {
            this.log('warn', 'Failed to check existing storage access', error);
            // Continue to request access despite check failure
        }

        // 4. Request storage access (must be in user gesture)
        try {
            await this.withTimeout(doc.requestStorageAccess(), timeoutMs);
        } catch (error: any) {
            if (error?.name === 'NotAllowedError') {
                this.log('warn', 'Storage access denied by user or browser policy');
                return { ok: false, reason: 'denied' };
            }
            if (error?.name === 'TimeoutError') {
                this.log('error', `Storage access request timed out after ${timeoutMs}ms`);
                return { ok: false, reason: 'timeout' };
            }
            this.log('error', 'Unknown error requesting storage access', error);
            return { ok: false, reason: 'unknown' };
        }

        // 5. Verify access was actually granted
        try {
            const hasAccess = await doc.hasStorageAccess();
            if (hasAccess) {
                this.log('info', 'Storage access successfully granted');
                return { ok: true, reason: 'granted' };
            } else {
                this.log('warn', 'Storage access request succeeded but verification failed');
            }
        } catch (error) {
            this.log('error', 'Failed to verify storage access after request', error);
        }

        return { ok: false, reason: 'unknown' };
    }

    /**
     * Wraps a promise with a timeout
     * @param promise - The promise to wrap
     * @param ms - Timeout in milliseconds
     * @returns Promise that rejects with TimeoutError if timeout is exceeded
     */
    private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const timer = setTimeout(
                () => reject(new DOMException('Timeout', 'TimeoutError')),
                ms
            );

            promise
                .then(value => {
                    clearTimeout(timer);
                    resolve(value);
                })
                .catch(error => {
                    clearTimeout(timer);
                    reject(error);
                });
        });
    }

    /**
     * Centralized logging with optional disable
     */
    private log(level: 'info' | 'warn' | 'error', message: string, error?: any): void {
        if (!this.enableLogging) return;

        const prefix = '[Storage Access]';
        const fullMessage = `${prefix} ${message}`;

        switch (level) {
            case 'info':
                console.info(fullMessage, error ?? '');
                break;
            case 'warn':
                console.warn(fullMessage, error ?? '');
                break;
            case 'error':
                console.warn(fullMessage, error ?? '');
                break;
        }
    }
}
